// =====================================================================================
// INVRT Radar - web console - DataTable (canonical mirror, Story PS1)
//
// Mirror of the INVRT Design System's DataTable (components/DataTable/DataTable.jsx +
// DataTable.d.ts), styled with this console's audited ui/table primitives exactly as N3
// used them as the pattern carrier. The canonical law holds: interaction states are the
// component's real job (hover, focused, keyboard-activatable rows), numeric columns are
// mono + tabular + right-aligned so a column of figures scans as a column, and the `empty`
// slot replaces the table rather than stacking beneath it.
//
// Documented deviation (deliberate subset, the pagination.tsx precedent): the canonical
// sort and selection contracts (SortState/onSortChange, selectable/BulkBar) are not
// ported because no PS1 consumer sorts or bulk-edits; the contract can be grown
// additively when a consumer arrives. `label` is added to give the table an accessible
// name, which every PS1 consumer and test needs.
// =====================================================================================

import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeaderCell, TableRow } from "./table";

export interface DataColumn {
  key: string;
  label: string;
  /** Right-aligns and switches the cell to mono tabular figures. */
  numeric?: boolean;
  width?: number | string;
}

export interface DataTableRow {
  id?: string | number;
  selectLabel?: string;
  [cell: string]: ReactNode | string | number | undefined;
}

export interface DataTableProps {
  columns?: DataColumn[];
  rows?: DataTableRow[];
  onRowClick?: (row: DataTableRow) => void;
  caption?: string;
  /** comfortable = the default · compact = for scanning thousands. */
  density?: "comfortable" | "compact";
  /** Header stays put while the body scrolls. For tables taller than a screen. */
  stickyHead?: boolean;
  /** Rendered instead of the table when there are no rows: pass an EmptyState. */
  empty?: ReactNode;
  /** PS1 extension: accessible name for the table. */
  label?: string;
  className?: string;
}

const DENSITY_CLASSES = {
  comfortable: "px-3 py-2.5",
  compact: "px-3 py-1.5",
} as const;

export function DataTable({
  columns = [],
  rows = [],
  onRowClick,
  caption,
  density = "comfortable",
  stickyHead,
  empty,
  label,
  className,
}: DataTableProps) {
  if (rows.length === 0 && empty) {
    return <div className={cn("w-full", className)}>{empty}</div>;
  }

  const rowKeyDown = (row: DataTableRow) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <Table aria-label={label} className={className}>
      {caption ? <TableCaption>{caption}</TableCaption> : null}
      <TableHead className={stickyHead ? "sticky top-0 z-10 bg-surface" : undefined}>
        <TableRow>
          {columns.map((column) => (
            <TableHeaderCell
              key={column.key}
              data-num={column.numeric ? "" : undefined}
              className={cn(DENSITY_CLASSES[density], column.numeric && "text-right")}
              {...(column.width ? { style: { width: column.width } } : {})}
            >
              {column.label}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow
            key={row.id ?? index}
            data-clickable={onRowClick ? "" : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            onKeyDown={onRowClick ? rowKeyDown(row) : undefined}
            className={cn(onRowClick && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary")}
          >
            {columns.map((column) => (
              <TableCell
                key={column.key}
                data-num={column.numeric ? "" : undefined}
                className={cn(
                  DENSITY_CLASSES[density],
                  column.numeric && "text-right font-mono tabular-nums",
                )}
              >
                {row[column.key] ?? null}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
